import React, {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, Route, Routes} from 'react-router-dom'
import {useQuery, useQueryClient} from 'react-query'
import {AzPageHeader} from '../../components/AzPageHeader'
import {KTCard, KTCardBody} from '../../../_dashboard/helpers'
import {toast} from 'react-toastify'
import dataAxios from '../../modules/auth/core/dataAxios'
import StickyLanguageSelector from '../../components/StickyLanguageSelector'
import BlogEditPage from './BlogEditPage'
import BlogCategoriesView from './BlogCategoriesView'
import {useConfirm} from '../../components/ConfirmProvider'

type BlogRow = {
  _id: string
  slug?: string
  status?: 'draft' | 'published'
  category?: string
  tags?: string[]
  publishedAt?: string
  viewCount?: number
  image?: {url?: string}
  translations?: Array<{language: string; title?: string; tags?: string[]}>
}

const listPageSize = 10

const BlogListView: React.FC = () => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const confirm = useConfirm()
  const [selectedLanguage, setSelectedLanguage] = useState('fr')
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published'>('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const {data: categories = []} = useQuery(['blog-list-categories', selectedLanguage], async () => {
    const res = await dataAxios.get('/blog-categories/public', {params: {language: selectedLanguage}})
    const rows = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []
    return rows.map((c: {slug: string; title: string}) => ({slug: c.slug, title: c.title}))
  })

  const {data: blogResponse, isFetching} = useQuery(
    ['admin-blogs-list', page, selectedLanguage, statusFilter, categoryFilter, search],
    async () => {
      const params: Record<string, string | number> = {
        page,
        size: listPageSize,
        language: 'all',
      }
      if (statusFilter !== 'all') params.status = statusFilter
      if (categoryFilter !== 'all') params.category = categoryFilter
      if (search.trim()) params.title = search.trim()
      const res = await dataAxios.get('/blogs', {params})
      return res.data
    },
    {keepPreviousData: true},
  )

  const listData: BlogRow[] = useMemo(() => {
    const raw = blogResponse?.data ?? []
    return Array.isArray(raw) ? raw : []
  }, [blogResponse])

  const totalItems = Number(blogResponse?.payload?.pagination?.totalItems ?? blogResponse?.count ?? 0)
  const totalPages = Math.max(
    1,
    Number(blogResponse?.payload?.pagination?.totalPages ?? (Math.ceil(totalItems / listPageSize) || 1)),
  )

  const filteredData = useMemo(() => {
    return listData.filter((blog) => {
      const translation = blog.translations?.find((t) => t.language === selectedLanguage) || blog.translations?.[0]
      const title = String(translation?.title || '').toLowerCase()
      return !search.trim() || title.includes(search.trim().toLowerCase())
    })
  }, [listData, search, selectedLanguage])

  const getTitle = (blog: BlogRow) =>
    blog.translations?.find((t) => t.language === selectedLanguage)?.title ||
    blog.translations?.[0]?.title ||
    '—'

  const getTags = (blog: BlogRow) => {
    const translationTags = blog.translations?.find((t) => t.language === selectedLanguage)?.tags
    if (Array.isArray(translationTags) && translationTags.length > 0) {
      return translationTags
    }
    const anyTranslated = (blog.translations || []).some((t) => {
      const tags = t.tags
      return Array.isArray(tags) && tags.length > 0
    })
    if (anyTranslated) {
      return Array.isArray(translationTags) ? translationTags : []
    }
    return blog.tags || []
  }

  const deleteBlog = async (blogId: string) => {
    const ok = await confirm({message: intl.formatMessage({id: 'BLOG.DELETE_CONFIRM'})})
    if (!ok) return
    try {
      await dataAxios.delete(`/blogs/${blogId}`)
      toast.success(intl.formatMessage({id: 'ADMIN.COMMON.SUCCESS.ITEM_DELETED_SUCCESSFULLY'}))
      queryClient.invalidateQueries('admin-blogs-list')
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Delete failed')
    }
  }

  const start = totalItems > 0 ? page * listPageSize + 1 : 0
  const end = Math.min((page + 1) * listPageSize, totalItems || filteredData.length)

  return (
    <>
      {/* Hero toolbar */}
      <div className='card border-0 shadow-sm mb-8 overflow-hidden'>
        <div className='card-body d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-5 p-6 p-lg-10'>
          <div className='d-flex align-items-start gap-4'>
            <div className='symbol symbol-50px symbol-circle bg-light-primary d-none d-sm-flex'>
              <span className='symbol-label'>
                <i className='bi bi-journal-richtext fs-2 text-primary' />
              </span>
            </div>
            <div>
              <h1 className='fs-2 fw-bold text-gray-900 mb-2'>{intl.formatMessage({id: 'BLOG.LIST_TITLE'})}</h1>
              <p className='text-gray-600 fs-6 fw-semibold mb-0'>
                {intl.formatMessage({id: 'BLOG.LIST_SUBTITLE'})}
              </p>
            </div>
          </div>
          <div className='d-flex flex-wrap gap-3'>
            <Link to='/apps/blog-management/categories' className='btn btn-light-primary fw-semibold px-6 py-3'>
              <i className='bi bi-tags me-2' />
              {intl.formatMessage({id: 'BLOG.CATEGORIES.TITLE', defaultMessage: 'Categories'})}
            </Link>
            <Link to='/apps/blog-management/new' className='btn btn-primary fw-semibold px-8 py-3'>
              <i className='bi bi-plus-lg me-2' />
              {intl.formatMessage({id: 'BLOG.NEW_ARTICLE'})}
            </Link>
          </div>
        </div>
        <div className='h-5px bg-primary' style={{opacity: 0.12}} />
      </div>

      {/* Filters */}
      <KTCard className='mb-8 shadow-sm'>
        <KTCardBody className='py-6'>
          <div className='d-flex align-items-center gap-2 mb-6'>
            <span className='bullet bullet-vertical h-30px bg-primary' />
            <h3 className='fs-4 fw-bold text-gray-900 mb-0'>{intl.formatMessage({id: 'BLOG.FILTERS'})}</h3>
          </div>
          <div className='row g-5 align-items-end'>
            <div className='col-sm-6 col-xl-3'>
              <label className='form-label fw-semibold text-gray-700'>{intl.formatMessage({id: 'BLOG.STATUS'})}</label>
              <div className='position-relative'>
                <i className='bi bi-funnel fs-6 text-gray-500 position-absolute top-50 translate-middle-y ms-4' style={{pointerEvents: 'none'}} />
                <select
                  className='form-select form-select-solid ps-10'
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as 'all' | 'draft' | 'published')
                    setPage(0)
                  }}
                >
                  <option value='all'>{intl.formatMessage({id: 'REVIEWS.FILTER.ALL'})}</option>
                  <option value='draft'>{intl.formatMessage({id: 'BLOG.DRAFT'})}</option>
                  <option value='published'>{intl.formatMessage({id: 'BLOG.PUBLISHED'})}</option>
                </select>
              </div>
            </div>
            <div className='col-sm-6 col-xl-3'>
              <label className='form-label fw-semibold text-gray-700'>{intl.formatMessage({id: 'BLOG.CATEGORY'})}</label>
              <select
                className='form-select form-select-solid'
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value)
                  setPage(0)
                }}
              >
                <option value='all'>{intl.formatMessage({id: 'REVIEWS.FILTER.ALL'})}</option>
                {categories.map((category: {slug: string; title: string}) => (
                  <option key={category.slug} value={category.slug}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-sm-12 col-xl-4'>
              <label className='form-label fw-semibold text-gray-700'>{intl.formatMessage({id: 'COMMON.SEARCH'})}</label>
              <div className='position-relative'>
                <i className='bi bi-search fs-6 text-gray-500 position-absolute top-50 translate-middle-y ms-4' style={{pointerEvents: 'none'}} />
                <input
                  type='search'
                  className='form-control form-control-solid ps-10'
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(0)
                  }}
                  placeholder={intl.formatMessage({id: 'BLOG.SEARCH_PLACEHOLDER'})}
                />
              </div>
            </div>
          </div>
          <div className='separator separator-dashed my-8' />
          <StickyLanguageSelector
            variant='compact'
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
          />
        </KTCardBody>
      </KTCard>

      {/* Table */}
      <KTCard className='shadow-sm'>
        <div className='card-header border-0 pt-6 pb-4'>
          <div className='card-title flex-column align-items-start'>
            <span className='fs-4 fw-bold text-gray-900 d-flex align-items-center gap-3'>
              {intl.formatMessage({id: 'BLOG.LIST_TITLE'})}
              {isFetching ? (
                <span className='spinner-border spinner-border-sm text-primary' role='status' />
              ) : (
                <span className='badge badge-light-primary fs-8 fw-bold'>{totalItems}</span>
              )}
            </span>
            <span className='text-muted fs-7 fw-semibold mt-1'>
              {intl.formatMessage({id: 'REVIEWS.PAGINATION.INFO'}, {from: start, to: end, total: totalItems})}
            </span>
          </div>
        </div>
        <KTCardBody className='pt-0'>
          <style>{`
            .blog-admin-table tbody tr.blog-admin-table__row {
              transition: background-color 0.15s ease, box-shadow 0.15s ease;
            }
            .blog-admin-table tbody tr.blog-admin-table__row:hover {
              background-color: rgba(var(--bs-primary-rgb), 0.05);
              box-shadow: inset 3px 0 0 0 var(--bs-primary);
            }
            .blog-admin-table tbody td {
              padding-top: 0.65rem;
              padding-bottom: 0.65rem;
              vertical-align: middle;
            }
            .blog-admin-table th.blog-admin-table__actions,
            .blog-admin-table td.blog-admin-table__actions {
              white-space: nowrap;
              min-width: 11rem;
              width: 1%;
            }
            .blog-admin-table__actions-inner {
              display: flex;
              flex-direction: row;
              flex-wrap: nowrap;
              align-items: center;
              justify-content: flex-end;
              gap: 0.25rem;
            }
          `}</style>
          <div
            className={`table-responsive position-relative ${isFetching ? 'opacity-50' : ''}`}
            style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%'}}
          >
            <table
              className='table table-hover align-middle gs-0 gy-3 blog-admin-table'
              style={{minWidth: 900, width: 'max-content'}}
            >
              <thead>
                <tr className='fw-bold text-muted text-uppercase fs-7 border-bottom border-gray-200'>
                  <th className='min-w-70px ps-4'>{intl.formatMessage({id: 'BLOG.TABLE_THUMB'})}</th>
                  <th className='min-w-200px'>{intl.formatMessage({id: 'TOUR.FIELDS.TITLE'})}</th>
                  <th className='min-w-120px'>{intl.formatMessage({id: 'BLOG.CATEGORY'})}</th>
                  <th className='min-w-150px'>{intl.formatMessage({id: 'BLOG.TAGS'})}</th>
                  <th className='min-w-100px'>{intl.formatMessage({id: 'BLOG.STATUS'})}</th>
                  <th className='min-w-110px'>{intl.formatMessage({id: 'BLOG.PUBLISH_DATE'})}</th>
                  <th className='min-w-80px'>{intl.formatMessage({id: 'BLOG.VIEWS'})}</th>
                  <th className='text-end pe-4 blog-admin-table__actions'>
                    {intl.formatMessage({id: 'COMMON.ACTIONS'})}
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isFetching && filteredData.length === 0 && (
                  <tr>
                    <td colSpan={8} className='text-center py-16'>
                      <div className='d-flex flex-column align-items-center'>
                        <div className='symbol symbol-80px symbol-circle bg-light mb-5'>
                          <span className='symbol-label'>
                            <i className='bi bi-journal-text fs-2x text-gray-500' />
                          </span>
                        </div>
                        <p className='text-gray-700 fw-semibold fs-5 mb-2'>{intl.formatMessage({id: 'COMMON.EMPTY_DATA'})}</p>
                        <p className='text-muted fs-7 mb-6'>{intl.formatMessage({id: 'BLOG.LIST_SUBTITLE'})}</p>
                        <Link to='/apps/blog-management/new' className='btn btn-sm btn-primary'>
                          <i className='bi bi-plus-lg me-2' />
                          {intl.formatMessage({id: 'BLOG.NEW_ARTICLE'})}
                        </Link>
                      </div>
                    </td>
                  </tr>
                )}
                {filteredData.map((blog) => {
                  const imageUrl = blog.image?.url
                    ? blog.image.url.includes('/images/')
                      ? `${process.env.REACT_APP_API_URL_IMAGE || ''}${blog.image.url}`
                      : blog.image.url
                    : ''
                  return (
                    <tr key={blog._id} className='fs-6 blog-admin-table__row border-bottom border-gray-200'>
                      <td className='ps-4'>
                        {imageUrl ? (
                          <div
                            className='rounded-4 overflow-hidden shadow-sm border border-gray-200'
                            style={{width: 56, height: 56}}
                          >
                            <img src={imageUrl} alt='' className='w-100 h-100 object-fit-cover' />
                          </div>
                        ) : (
                          <div
                            className='rounded-4 d-flex align-items-center justify-content-center border border-dashed border-gray-300 bg-light'
                            style={{width: 56, height: 56}}
                          >
                            <i className='bi bi-image text-gray-400 fs-2' />
                          </div>
                        )}
                      </td>
                      <td>
                        <span className='text-gray-900 fw-bold fs-6 text-truncate d-inline-block' style={{maxWidth: 300}}>
                          {getTitle(blog)}
                        </span>
                      </td>
                      <td>
                        {blog.category ? (
                          <span className='badge badge-light-secondary fw-semibold px-3 py-2'>{blog.category}</span>
                        ) : (
                          <span className='text-muted fs-7'>—</span>
                        )}
                      </td>
                      <td>
                        <div className='d-flex flex-wrap gap-1'>
                          {getTags(blog).length === 0 ? (
                            <span className='text-muted fs-7'>—</span>
                          ) : (
                            <>
                              {getTags(blog).slice(0, 3).map((tag) => (
                                <span key={tag} className='badge badge-light-info fs-8 fw-semibold px-2 py-1'>
                                  {tag}
                                </span>
                              ))}
                              {getTags(blog).length > 3 && (
                                <span className='badge badge-light fs-8'>+{getTags(blog).length - 3}</span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge rounded-pill fs-8 fw-bold px-3 py-2 ${
                            blog.status === 'published' ? 'badge-light-success text-success' : 'badge-light-warning text-gray-800'
                          }`}
                        >
                          {blog.status === 'published'
                            ? intl.formatMessage({id: 'BLOG.PUBLISHED'})
                            : intl.formatMessage({id: 'BLOG.DRAFT'})}
                        </span>
                      </td>
                      <td className='text-gray-700 fw-semibold fs-7'>
                        {blog.publishedAt ? new Date(blog.publishedAt).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <span className='badge badge-light-dark fs-8 fw-bold px-2 py-2'>{blog.viewCount ?? 0}</span>
                      </td>
                      <td className='text-end pe-4 blog-admin-table__actions'>
                        <div className='blog-admin-table__actions-inner rounded-2 bg-light py-1 px-2'>
                          <Link
                            to={`/apps/blog-management/${blog._id}`}
                            className='btn btn-icon btn-sm btn-light-primary flex-shrink-0'
                            title={intl.formatMessage({id: 'ACTION.EDIT'})}
                          >
                            <i className='bi bi-pencil-square fs-6' />
                          </Link>
                          <button
                            type='button'
                            className='btn btn-icon btn-sm btn-light-danger flex-shrink-0'
                            title={intl.formatMessage({id: 'ACTION.DELETE'})}
                            onClick={() => deleteBlog(blog._id)}
                          >
                            <i className='bi bi-trash fs-6' />
                          </button>
                          <a
                            href={`/blog/${blog.slug || ''}`}
                            target='_blank'
                            rel='noreferrer'
                            className='btn btn-icon btn-sm btn-light flex-shrink-0'
                            title={intl.formatMessage({id: 'BLOG.VIEW_SITE'})}
                          >
                            <i className='bi bi-box-arrow-up-right fs-6' />
                          </a>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {totalItems > 0 && totalPages > 1 && (
            <div
              className='bo-pagination'
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                width: '100%',
                marginTop: 16,
                padding: '8px 0',
              }}
            >
              <button
                type='button'
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                style={{
                  minWidth: 44,
                  height: 40,
                  border: 'none',
                  borderRadius: 10,
                  background: '#f2f4f6',
                  fontWeight: 700,
                  fontSize: 16,
                  opacity: page <= 0 ? 0.55 : 1,
                }}
              >
                ‹
              </button>
              <span style={{fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap'}}>
                {page + 1} / {totalPages}
              </span>
              <button
                type='button'
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{
                  minWidth: 44,
                  height: 40,
                  border: 'none',
                  borderRadius: 10,
                  background: '#f2f4f6',
                  fontWeight: 700,
                  fontSize: 16,
                  opacity: page + 1 >= totalPages ? 0.55 : 1,
                }}
              >
                ›
              </button>
            </div>
          )}
        </KTCardBody>
      </KTCard>
    </>
  )
}

const BlogManagementPage: React.FC = () => {
  const intl = useIntl()

  return (
    <>
      <AzPageHeader title={intl.formatMessage({id: 'BLOG.TITLE'})} />
      {/*
        Parent : `apps/blog-management/*` (PrivateRoutes). useRoutes compare les enfants
        au chemin *restant* (ex. `/` pour la liste), pas à `/apps/blog-management/...`.
        D’où index + `:id` (inclut l’édition `.../new`).
      */}
      <Routes>
        <Route index element={<BlogListView />} />
        <Route path='categories' element={<BlogCategoriesView />} />
        <Route path=':id' element={<BlogEditPage />} />
      </Routes>
    </>
  )
}

export default BlogManagementPage
